import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { 
  UserGroupIcon,
  AcademicCapIcon,
  ArrowLeftIcon,
  PencilIcon,
  PlusIcon,
  CheckCircleIcon,
  XMarkIcon,
  BuildingOffice2Icon,
  UserIcon
} from "@heroicons/react/24/outline";
import { HsePageChrome } from "../../../../components/dashboard/hse/HsePageChrome";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { ResourceEmpty } from "../../../../components/utils/ResourceEmpty";
import { errorStateVariantFromError } from "../../../../components/utils/errorStateUtils";
import RoleOnly, { useUserHasAccess } from "../../../../routes/roleOnly";
import { getRoleCompetencyMatrix, updateRoleCompetencyRequirements, getRole } from "../../../../api/hse/competency";
import { getCompetencyDefinitions } from "../../../../api/hse/competency";
import { PMDialog } from "../../../../components/utils/PMDialog";
import { toast } from "react-hot-toast";
import { CompetencySubnav } from "../../../../components/dashboard/hse/competency/CompetencySubnav";

/**
 * HSE/KS Competency Matrix Role - Competency matrix for specific role
 * ISO 9001:2015 Clause 7.2 - Competence
 */
export const HseKsCompetencyMatrixRole = () => {
  const { t } = useTranslation();
  const { role: roleId } = useParams();
  const navigate = useNavigate();
  
  const [role, setRole] = useState(null);
  const [requiredCompetencies, setRequiredCompetencies] = useState([]);
  const [availableCompetencies, setAvailableCompetencies] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [errorVariant, setErrorVariant] = useState("load_error");
  const [saving, setSaving] = useState(false);
  const [addCompetencyDialogOpen, setAddCompetencyDialogOpen] = useState(false);
  
  const canManage = useUserHasAccess({ role: "hse_ks_competency_manage", action: "read" });

  useEffect(() => {
    if (canManage) {
      fetchRoleData();
      fetchAvailableCompetencies();
    }
  }, [roleId, canManage]);

  const fetchRoleData = async () => {
    try {
      setLoading(true);
      setError(false);
      
      const roleResponse = await getRole(roleId);
      setRole(roleResponse.role || roleResponse);
      
      const matrixResponse = await getRoleCompetencyMatrix(roleId);
      setRequiredCompetencies(matrixResponse.required_competencies || []);
      setEmployees(matrixResponse.employees || []);
    } catch (err) {
      console.error("Error fetching role data:", err);
      const variant = errorStateVariantFromError(err);
      setError(true);
      setErrorVariant(variant);
      if (variant === "load_error") {
        toast.error(t("common.error_state.load_error.title"));
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableCompetencies = async () => {
    try {
      const response = await getCompetencyDefinitions({ is_active: true });
      setAvailableCompetencies(response.competency_definitions || response.definitions || []);
    } catch (err) {
      console.error("Error fetching competencies:", err);
    }
  };

  const handleToggleCompetency = async (competencyId) => {
    if (!canManage) return;
    
    const isRequired = requiredCompetencies.some(c => c.id === competencyId || c.competency_definition_id === competencyId);
    const newRequiredIds = isRequired
      ? requiredCompetencies.filter(c => c.id !== competencyId && c.competency_definition_id !== competencyId).map(c => c.id || c.competency_definition_id)
      : [...requiredCompetencies.map(c => c.id || c.competency_definition_id), competencyId];
    
    try {
      setSaving(true);
      await updateRoleCompetencyRequirements(roleId, { competency_definition_ids: newRequiredIds });
      await fetchRoleData();
      toast.success(t("Kompetansekrav oppdatert"));
    } catch (err) {
      console.error("Error updating requirements:", err);
      toast.error(t("Kunne ikke oppdatere kompetansekrav"));
    } finally {
      setSaving(false);
    }
  };

  const handleAddCompetency = async (competencyId) => {
    if (!competencyId) return;
    
    const isAlreadyRequired = requiredCompetencies.some(
      c => c.id === competencyId || c.competency_definition_id === competencyId
    );
    
    if (isAlreadyRequired) {
      toast.error(t("Denne kompetansen er allerede påkrevd"));
      return;
    }
    
    const newRequiredIds = [
      ...requiredCompetencies.map(c => c.id || c.competency_definition_id),
      competencyId
    ];
    
    try {
      setSaving(true);
      await updateRoleCompetencyRequirements(roleId, { competency_definition_ids: newRequiredIds });
      await fetchRoleData();
      setAddCompetencyDialogOpen(false);
      toast.success(t("Kompetanse lagt til"));
    } catch (err) {
      console.error("Error adding requirement:", err);
      toast.error(t("Kunne ikke legge til kompetanse"));
    } finally {
      setSaving(false);
    }
  };

  if (!canManage) {
    navigate("/dashboard/hse-ks/competency/matrix");
    return null;
  }

  if (loading) {
    return <ResourceLoading size="base" />;
  }

  if (error) {
    return (
      <ResourceError
        variant={errorVariant}
        onRetry={errorVariant === "load_error" ? fetchRoleData : undefined}
        backTo="/dashboard/hse-ks/competency/matrix"
      />
    );
  }

  if (!role) {
    return (
      <ResourceError
        variant="not_found"
        backTo="/dashboard/hse-ks/competency/matrix"
      />
    );
  }

  return (
    <HsePageChrome
      title={`${t("Kompetansematrise")} - ${role.name}`}
      subtitle={t("Kompetansematrise for rolle")}
    >
      <div className="mx-auto mt-8 max-w-7xl sm:px-6 lg:px-8">
        <CompetencySubnav />
        <div className="space-y-6">
          {/* Back Button */}
          <div>
            <button
              onClick={() => navigate("/dashboard/hse-ks/competency/matrix")}
              className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-1" />
              {t("Tilbake til kompetansematrise")}
            </button>
          </div>

          {/* Role Info */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <UserGroupIcon className="h-10 w-10 text-blue-600 mr-4" />
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">{role.name}</h3>
                    {role.department_name || role.department ? (
                      <p className="text-sm text-gray-500 flex items-center mt-1">
                        <BuildingOffice2Icon className="h-4 w-4 mr-1" />
                        {role.department_name || role.department}
                      </p>
                    ) : null}
                    {role.description && (
                      <p className="text-sm text-gray-500 mt-1">{role.description}</p>
                    )}
                  </div>
                </div>
                {canManage && (
                  <Link
                    to={`/dashboard/hse-ks/competency/roles/${roleId}/edit`}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <PencilIcon className="h-4 w-4 mr-1" />
                    {t("Rediger rolle")}
                  </Link>
                )}
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <AcademicCapIcon className="h-8 w-8 text-blue-500" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">{t("Påkrevde kompetanser")}</dt>
                      <dd className="text-lg font-semibold text-gray-900">{requiredCompetencies.length}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <UserIcon className="h-8 w-8 text-green-500" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">{t("Ansatte")}</dt>
                      <dd className="text-lg font-semibold text-gray-900">{employees.length}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <CheckCircleIcon className="h-8 w-8 text-purple-500" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">{t("Kompetente ansatte")}</dt>
                      <dd className="text-lg font-semibold text-gray-900">
                        {employees.filter(e => e.competency_status === "complete").length}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Required Competencies */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">{t("Påkrevde kompetanser")}</h3>
                {canManage && (
                  <button
                    onClick={() => setAddCompetencyDialogOpen(true)}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    {t("Legg til kompetanse")}
                  </button>
                )}
              </div>
              
              {requiredCompetencies.length === 0 ? (
                <ResourceEmpty message={t("Ingen påkrevde kompetanser")} />
              ) : (
                <div className="space-y-3">
                  {requiredCompetencies.map((competency) => {
                    const isRequired = true;
                    return (
                      <div
                        key={competency.id || competency.competency_definition_id}
                        className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                      >
                        <div className="flex items-center flex-1">
                          <AcademicCapIcon className="h-5 w-5 text-blue-600 mr-3" />
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">
                              {competency.competency_definition?.name || competency.name}
                            </div>
                            {competency.competency_definition?.description && (
                              <div className="text-sm text-gray-500 mt-1">
                                {competency.competency_definition.description}
                              </div>
                            )}
                          </div>
                        </div>
                        {canManage && (
                          <button
                            onClick={() => handleToggleCompetency(competency.id || competency.competency_definition_id)}
                            disabled={saving}
                            className={`ml-4 px-3 py-1 text-sm font-medium rounded-md ${
                              isRequired
                                ? "bg-red-100 text-red-800 hover:bg-red-200"
                                : "bg-green-100 text-green-800 hover:bg-green-200"
                            }`}
                          >
                            {isRequired ? t("Fjern") : t("Legg til")}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Employees with this role */}
          {employees.length > 0 && (
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {t("Ansatte med denne rollen")} ({employees.length})
                </h3>
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                          {t("Ansatt")}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t("Kompetanse status")}
                        </th>
                        <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                          <span className="sr-only">{t("Handlinger")}</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {employees.map((employee) => (
                        <tr key={employee.id} className="hover:bg-gray-50">
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                            <div className="flex items-center">
                              <UserIcon className="h-5 w-5 text-blue-600 mr-3" />
                              <div className="font-medium text-gray-900">
                                {employee.name || employee.email}
                              </div>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm">
                            {employee.competency_status === "complete" ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                <CheckCircleIcon className="h-3 w-3 mr-1" />
                                {t("Kompetent")}
                              </span>
                            ) : employee.competency_status === "partial" ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                {t("Delvis")}
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                <XMarkIcon className="h-3 w-3 mr-1" />
                                {t("Ikke kompetent")}
                              </span>
                            )}
                          </td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            <Link
                              to={`/dashboard/hse-ks/competency/employees/${employee.id}`}
                              className="text-indigo-600 hover:text-indigo-900"
                            >
                              {t("Vis")}
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Competency Dialog */}
      <PMDialog
        open={addCompetencyDialogOpen}
        onClose={() => setAddCompetencyDialogOpen(false)}
        title={t("Legg til kompetansekrav")}
        size="2xl"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            {t("Velg kompetanse som skal være påkrevd for denne rollen")}
          </p>
          
          {availableCompetencies.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-sm text-gray-500">{t("Ingen kompetansedefinisjoner tilgjengelig")}</p>
              <button
                onClick={() => {
                  setAddCompetencyDialogOpen(false);
                  navigate("/dashboard/hse-ks/competency/definitions/new");
                }}
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                {t("Opprett ny kompetanse")}
              </button>
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto space-y-2">
              {availableCompetencies
                .filter(comp => {
                  // Filter out competencies that are already required
                  return !requiredCompetencies.some(
                    req => req.id === comp.id || req.competency_definition_id === comp.id
                  );
                })
                .map((competency) => (
                  <button
                    key={competency.id}
                    onClick={() => handleAddCompetency(competency.id)}
                    disabled={saving}
                    className="w-full text-left p-4 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-indigo-300 transition-colors disabled:opacity-50"
                  >
                    <div className="flex items-center">
                      <AcademicCapIcon className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0" />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{competency.name}</div>
                        {competency.description && (
                          <div className="text-sm text-gray-500 mt-1">{competency.description}</div>
                        )}
                        {competency.type && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 mt-2">
                            {competency.type}
                          </span>
                        )}
                      </div>
                      <PlusIcon className="h-5 w-5 text-gray-400 ml-3" />
                    </div>
                  </button>
                ))}
            </div>
          )}
          
          <div className="flex justify-end pt-4 border-t">
            <button
              onClick={() => setAddCompetencyDialogOpen(false)}
              className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              {t("Avbryt")}
            </button>
          </div>
        </div>
      </PMDialog>
    </HsePageChrome>
  );
};

